/**
 * Resource reservation mutations (docs/specs/resource-reservation.md) — reserve a
 * Resource for an Account, or release the reservation. RM/ADMIN only; the server enforces it.
 *
 * A reservation changes what every Resource-facing surface shows — the directory badge, the
 * profile's Guidance Outlook and matrix, the Schedule, picker ordering, and plan load bars — so both
 * mutations invalidate each of those families.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { jsonFetch } from "@/lib/json-fetch";
import {
  atlasSuggestKeys,
  dashboardHeatmapKeys,
  guidanceKeys,
  resourceDirectoryKeys,
  resourcePlanKeys,
} from "@/lib/query";
import type {
  ReserveResourceInput,
  ResourceReservationView,
} from "@/schemas/resource-reservations";

export type {
  ReserveResourceInput,
  ResourceReservationView,
} from "@/schemas/resource-reservations";

function useInvalidateReservationViews() {
  const qc = useQueryClient();
  // Not awaited by the mutations: a refetch that is slow or failing (NOS guidance when the
  // gateway is down) must not keep the dialog in "Saving…" after the write has landed.
  return () => {
    for (const queryKey of [
      resourceDirectoryKeys.all,
      guidanceKeys.all,
      atlasSuggestKeys.all,
      dashboardHeatmapKeys.all,
      resourcePlanKeys.all,
    ]) {
      void qc.invalidateQueries({ queryKey });
    }
  };
}

export function useReserveResource(resourceId: string) {
  const invalidate = useInvalidateReservationViews();
  return useMutation({
    mutationFn: (input: ReserveResourceInput) =>
      jsonFetch<ResourceReservationView>(`/api/dashboard/resources/${resourceId}/reservation`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: (view) => {
      invalidate();
      toast.success(`Reserved for ${view.accountName}`);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not save the reservation");
    },
  });
}

export function useReleaseResourceReservation(resourceId: string) {
  const invalidate = useInvalidateReservationViews();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/dashboard/resources/${resourceId}/reservation`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const body: unknown = await res.json().catch(() => ({}));
        const message =
          typeof body === "object" &&
          body !== null &&
          "error" in body &&
          typeof body.error === "string"
            ? body.error
            : `Request failed: ${res.status}`;
        throw new Error(message);
      }
    },
    onSuccess: () => {
      invalidate();
      toast.success("Reservation released");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not release the reservation");
    },
  });
}
